import isEqual from "lodash.isequal";

import { v4 } from "uuid";
import type { z } from "zod/v4";

import {
  ANCHOR_KEY_PATTERN,
  ComponentPlacementLocation,
  OnsiteUpsellConfig,
} from "@intelligems/ig-types/dtos/components";
import { InsertType } from "@intelligems/ig-types/dtos/onsite-edits";
import {
  OnsiteUpsellComponentSchema,
  SharedOnsiteUpsellComponent,
  SharedOnsiteUpsellComponentSchema,
} from "@intelligems/ig-types/schemas/visual-builder/mod-schemas";
import { ONSITE_UPSELL_TEMPLATES } from "@intelligems/ig-utils/onsite-upsell-template";
import { isValidSelector } from "@src/store/slices/experienceBuilder/common/utils/html/isValidSelector";
import { pickSharedFields } from "@src/store/slices/experienceBuilder/common/utils/pickSharedFields";
import { sharedFromAiFormat } from "@src/store/slices/experienceBuilder/common/utils/sharedFromAiFormat";
import {
  ModHandler,
  OnsiteUpsellComponent,
  PlacementMode,
} from "@src/store/slices/experienceBuilder/types";
import {
  canonicalRulesToFlat,
  flatRulesToCanonical,
} from "@src/store/slices/experienceBuilder/component/utils/onsiteUpsell/ruleMapping";

export const DEFAULT_PLACEMENT: Record<
  ComponentPlacementLocation,
  { selector: string; insertType: InsertType }
> = {
  // Shopify's installments form also posts to /cart/add, comes first and is
  // often hidden; only the first match renders.
  pdp: {
    selector: 'form[action*="/cart/add"]:has([type="submit"], [name="add"])',
    insertType: "afterend",
  },
  cart: { selector: ".cart__footer", insertType: "beforebegin" },
};

/**
 * Placement is one of two modes and the panel toggles between them. Switching
 * must CLEAR the other mode's fields: leaving a stale selector behind would
 * fail the schema's exactly-one-mode check with an error pointing at a field
 * the merchant can no longer see.
 */
export const DEFAULT_ANCHOR_KEY = "product-recommendations";

/** Which of the two placement modes a mod is currently in. */
export function placementMode(mod: { anchorKey?: string }): PlacementMode {
  return mod.anchorKey ? "themeBlock" : "selector";
}

export function placementForMode(
  mode: PlacementMode,
  location: ComponentPlacementLocation,
  previous?: { anchorKey?: string; selector?: string; insertType?: InsertType }
): Pick<OnsiteUpsellComponent, "anchorKey" | "selector" | "insertType"> {
  if (mode === "themeBlock") {
    return {
      anchorKey: previous?.anchorKey || DEFAULT_ANCHOR_KEY,
      selector: undefined,
      insertType: undefined,
    };
  }
  return {
    anchorKey: undefined,
    selector: previous?.selector || DEFAULT_PLACEMENT[location].selector,
    insertType: previous?.insertType ?? DEFAULT_PLACEMENT[location].insertType,
  };
}

type UpsellModFields = Pick<
  OnsiteUpsellComponent,
  | "location"
  | "anchorKey"
  | "selector"
  | "insertType"
  | "rules"
  | "canonicalRules"
  | "agenticLogicPlacementId"
  | "agenticLogicRuleId"
>;
type UpsellConfigFields = Pick<
  OnsiteUpsellConfig,
  "placement" | "rules" | "agenticLogicPlacementId" | "agenticLogicRuleId"
>;

export const upsellConfigFields = (
  mod: UpsellModFields
): UpsellConfigFields => ({
  // Only the active mode's keys, or `isEqual` reports unsaved changes.
  placement: {
    ...(mod.anchorKey
      ? { anchorKey: mod.anchorKey }
      : { selector: mod.selector, insertType: mod.insertType }),
    location: mod.location,
  },
  // Keep the original canonical rules (ids, priority) when unchanged.
  rules:
    mod.canonicalRules &&
    isEqual(canonicalRulesToFlat(mod.canonicalRules), mod.rules)
      ? mod.canonicalRules
      : flatRulesToCanonical(mod.rules, mod.location, mod.canonicalRules),
  // Dropping these would detach an engine-backed component on save.
  ...(mod.agenticLogicPlacementId === undefined
    ? {}
    : { agenticLogicPlacementId: mod.agenticLogicPlacementId }),
  ...(mod.agenticLogicRuleId === undefined
    ? {}
    : { agenticLogicRuleId: mod.agenticLogicRuleId }),
});

export const upsellModFields = (
  config: UpsellConfigFields
): UpsellModFields => ({
  location: config.placement.location,
  anchorKey: config.placement.anchorKey,
  selector: config.placement.selector,
  insertType: config.placement.insertType,
  rules: canonicalRulesToFlat(config.rules),
  canonicalRules: config.rules,
  agenticLogicPlacementId: config.agenticLogicPlacementId ?? null,
  agenticLogicRuleId: config.agenticLogicRuleId ?? null,
});

// Schema errors plus selector checks the schema can't express.
export const upsellErrors = (
  result: z.ZodSafeParseResult<unknown>,
  mod: Pick<UpsellModFields, "anchorKey" | "selector">
) => {
  const errors: Record<string, string> = {};
  if (!result.success) {
    for (const issue of result.error.issues) {
      errors[issue.path.join(".") || "form"] = issue.message;
    }
  }
  if (mod.selector && !isValidSelector(mod.selector)) {
    errors.selector = "Selector is invalid";
  }
  if (mod.anchorKey && !ANCHOR_KEY_PATTERN.test(mod.anchorKey)) {
    errors.anchorKey =
      "Use lowercase letters, numbers, dashes and underscores.";
  }
  return errors;
};

// The fields that make two upsell mods meaningfully different (ignores meta).
const meaningful = (mod: OnsiteUpsellComponent) => ({
  location: mod.location,
  anchorKey: mod.anchorKey,
  selector: mod.selector,
  insertType: mod.insertType,
  maxProducts: mod.maxProducts,
  excludeOutOfStock: mod.excludeOutOfStock,
  excludeItemsInCart: mod.excludeItemsInCart,
  excludePdpProduct: mod.excludePdpProduct,
  discount: mod.discount,
  rules: mod.rules,
  template: mod.template,
  description: mod.description,
  // The binding is part of what the merchant edits, so a rule change alone
  // has to enable Apply. The placement id is NOT here: it is stamped by the
  // server, so a re-read of the same component would otherwise read as an
  // unsaved change.
  agenticLogicRuleId: mod.agenticLogicRuleId,
});

export const onsiteUpsellModHandler: ModHandler<OnsiteUpsellComponent> = {
  normalize: (mod) => ({
    ...mod,
    anchorKey: mod.anchorKey?.trim() || undefined,
    selector: mod.selector?.trim(),
  }),

  convertFrom: (mod) => ({
    type: "component",
    dto: {
      id: mod.id,
      variationId: mod.variationId,
      config: {
        type: "onsiteUpsell",
        ...upsellConfigFields(mod),
        template: mod.template,
        maxProductLimit: mod.maxProducts,
        excludeOutOfStock: mod.excludeOutOfStock,
        excludeItemsInCart: mod.excludeItemsInCart,
        excludePdpProduct: mod.excludePdpProduct,
        // Match the persisted DTO, which omits `discount` when absent
        // (`UpsellDiscountSchema.optional()`); writing `undefined` would inflate
        // the key count and read as unsaved changes under lodash `isEqual`.
        ...(mod.discount ? { discount: mod.discount } : {}),
      },
    },
  }),

  convertTo: (conversion) => {
    if (conversion.type !== "component") {
      throw new Error("Expected component conversion");
    }
    const { id, variationId, config } = conversion.dto;
    if (config.type !== "onsiteUpsell") {
      throw new Error(`Expected onsiteUpsell config, got ${config.type}`);
    }
    return {
      type: "onsiteUpsell",
      category: "component",
      id,
      variationId,
      ...upsellModFields(config),
      maxProducts: config.maxProductLimit,
      excludeOutOfStock: config.excludeOutOfStock,
      excludeItemsInCart: config.excludeItemsInCart,
      excludePdpProduct: config.excludePdpProduct,
      discount: config.discount ?? null,
      template: config.template,
    };
  },

  getErrors: (mod) =>
    upsellErrors(
      OnsiteUpsellComponentSchema.safeParse(
        onsiteUpsellModHandler.toAiFormat(mod)
      ),
      mod
    ),

  hasChanges: (oldMod, newMod) =>
    !isEqual(meaningful(oldMod), meaningful(newMod)),

  isEquivalent: (a, b) =>
    b.type === "onsiteUpsell" &&
    a.location === b.location &&
    a.anchorKey === b.anchorKey &&
    a.selector === b.selector,

  defaults: () => {
    const rules: OnsiteUpsellComponent["rules"] = [
      { conditions: [], products: { source: "recommendations" } },
    ];
    return {
      id: v4(),
      type: "onsiteUpsell",
      category: "component",
      variationId: "",
      location: "pdp",
      selector: DEFAULT_PLACEMENT.pdp.selector,
      insertType: DEFAULT_PLACEMENT.pdp.insertType,
      maxProducts: 3,
      excludeOutOfStock: true,
      excludeItemsInCart: true,
      excludePdpProduct: true,
      discount: null,
      rules,
      canonicalRules: flatRulesToCanonical(rules, "pdp"),
      template: ONSITE_UPSELL_TEMPLATES.list,
    };
  },

  toAiFormat: (mod) =>
    pickSharedFields<SharedOnsiteUpsellComponent>(
      SharedOnsiteUpsellComponentSchema,
      mod
    ),

  fromAiFormat: (aiMod, context) => {
    const merged = {
      ...(context.existing ?? onsiteUpsellModHandler.defaults()),
      ...aiMod,
      ...sharedFromAiFormat(aiMod, context),
      category: "component" as const,
    };
    // A partial update can name one mode while the existing mod is in the
    // other, and the merge would carry both forward — an invalid mod. The
    // incoming mod decides, so drop whatever it did not name.
    const base = {
      ...merged,
      ...placementForMode(placementMode(aiMod), merged.location, aiMod),
    };
    return {
      ...base,
      canonicalRules: flatRulesToCanonical(
        base.rules,
        base.location,
        context.existing?.canonicalRules
      ),
    };
  },
};
